import React, { useEffect, useState } from "react";
import { Eye, EyeOff, KeyRound, Loader2, RefreshCcw, Save } from "lucide-react";

function PosDrawerPinModal({
  show,
  user,
  onClose,
  onSave,
  isSaving,
  existingPinData,
  isFetchingPin,
}) {
  const [pin, setPin] = useState("");
  const [showPin, setShowPin] = useState(true);

  const existingPin = existingPinData?.pin || "";
  const hasExistingPin = Boolean(existingPin);

  useEffect(() => {
    if (show) {
      setPin(existingPin || "");
      setShowPin(true);
    }
  }, [show, existingPin]);

  if (!show) return null;

  const handlePinChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setPin(value);
  };

  const handleGeneratePin = () => {
    const generatedPin = Math.floor(100000 + Math.random() * 900000).toString();
    setPin(generatedPin);
    setShowPin(true);
  };

  const handleSave = () => {
    if (pin.length !== 6) return;
    onSave(pin);
  };

  return (
    <>
  
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-4 montserrat-font-1">
      <div className="bg-white w-full max-w-[480px] rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-100 text-green-700 flex items-center justify-center">
              <KeyRound size={20} />
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-700">
                POS Drawer PIN
              </h2>
              <p className="text-xs text-gray-500 montserrat-font-2">
                {hasExistingPin
                  ? "View or update drawer access PIN"
                  : "Create drawer access PIN"}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            disabled={isSaving || isFetchingPin}
            className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          <div className="bg-gray-50 rounded-lg p-4 border text-sm space-y-1 montserrat-font-2">
            <p>
              <span className="font-semibold text-gray-700">Name:</span>{" "}
              {user?.user_name || "-"}
            </p>

            <p>
              <span className="font-semibold text-gray-700">Mobile:</span>{" "}
              {user?.user_mobile || "-"}
            </p>

            <p>
              <span className="font-semibold text-gray-700">FK User ID:</span>{" "}
              {user?.id || user?.userid || "-"}
            </p>

            {hasExistingPin && (
              <>
                <p>
                  <span className="font-semibold text-gray-700">Status:</span>{" "}
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs ${
                      existingPinData?.status === "Y"
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {existingPinData?.status === "Y" ? "Active" : "Inactive"}
                  </span>
                </p>

                <p>
                  <span className="font-semibold text-gray-700">
                    Updated On:
                  </span>{" "}
                  {existingPinData?.pin_updatedOn || "-"}
                </p>
              </>
            )}
          </div>

          {isFetchingPin ? (
            <div className="flex items-center justify-center gap-2 py-8 text-green-700">
              <Loader2 size={18} className="animate-spin" />
              <span className="text-sm font-medium">Checking existing PIN...</span>
            </div>
          ) : (
            <>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm text-green-700 font-medium">
                    {hasExistingPin ? "Existing / New 6 Digit PIN" : "Enter 6 Digit PIN"}
                  </label>

                  <button
                    type="button"
                    onClick={() => setShowPin((prev) => !prev)}
                    className="text-xs text-gray-500 hover:text-green-700 flex items-center gap-1"
                  >
                    {showPin ? <EyeOff size={14} /> : <Eye size={14} />}
                    {showPin ? "Hide" : "Show"}
                  </button>
                </div>

                <input
                  type={showPin ? "text" : "password"}
                  inputMode="numeric"
                  value={pin}
                  onChange={handlePinChange}
                  placeholder="Enter 6 digit PIN"
                  className="mt-2 w-full border rounded-lg px-4 py-3 text-center text-2xl tracking-[8px] font-semibold outline-none focus:border-green-600"
                />

                <p className="text-xs text-gray-500 mt-2 montserrat-font-2">
                  PIN must be exactly 6 digits. You can enter it manually or generate a new one.
                </p>
              </div>

              <button
                type="button"
                onClick={handleGeneratePin}
                disabled={isSaving}
                className="w-full flex items-center justify-center gap-2 border border-green-600 text-green-700 rounded-lg py-2.5 hover:bg-green-50 disabled:opacity-50"
              >
                <RefreshCcw size={16} />
                {hasExistingPin ? "Generate New PIN" : "Generate PIN"}
              </button>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t">
          <button
            onClick={onClose}
            disabled={isSaving || isFetchingPin}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            onClick={handleSave}
            disabled={pin.length !== 6 || isSaving || isFetchingPin}
            className={`px-6 py-2 rounded-lg flex items-center gap-2 ${
              pin.length === 6 && !isSaving && !isFetchingPin
                ? "bg-green-600 text-white hover:bg-green-700"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            <Save size={16} />
            {isSaving
              ? "Saving..."
              : hasExistingPin
              ? "Update PIN"
              : "Save PIN"}
          </button>
        </div>
      </div>
    </div>
    </>
  );
}

export default PosDrawerPinModal;